import { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Pencil, X } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

// Detect touch/mobile once.
const IS_MOBILE =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(max-width: 768px)").matches ||
    ("ontouchstart" in window && navigator.maxTouchPoints > 0));

/**
 * Searchable, mobile-friendly dropdown.
 *
 * Desktop: Radix Popover with a scrollable list.
 * Mobile:  Inline centered picker (NOT a portal). Rendered inside the
 *          Sheet's DOM subtree so it lives inside `react-remove-scroll`'s
 *          whitelist — that's the *only* way iOS Safari lets you finger-
 *          scroll inside an already-open Radix Sheet. Portals to
 *          document.body get their touchmove events preventDefault'd by
 *          react-remove-scroll's global handler → no scroll on iPhone.
 *
 *          Sizes to content up to a 70vh cap (so a 3-item Type list is
 *          a small popup, a 300-item Caliber list is capped).
 *
 * Pass `lookupOnly` to disable the pencil "type custom" toggle.
 */
export default function SearchableCombobox({
  value,
  onChange,
  options = [],
  testid,
  placeholder = "Select…",
  lookupOnly = false,
  inputType = "text",
  inputClassName = "dark-input",
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("select");
  const triggerRef = useRef(null);

  // Auto-drop into free-text mode if the saved value isn't in the option
  // list, so we never silently lose the user's data.
  useEffect(() => {
    if (lookupOnly) return;
    if (value && options.length && !options.map(String).includes(String(value))) {
      setMode("input");
    }
  }, [value, options, lookupOnly]);

  const allOptions = (options || []).map(String).filter((s) => s.trim() !== "");

  if (mode === "input" && !lookupOnly) {
    return (
      <div className="flex gap-1.5">
        <input
          type={inputType}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClassName} flex-1 h-10 px-3 rounded-sm border border-[#8B6A1F]/45 bg-[#F9F0D4] text-sm`}
          placeholder={placeholder}
          data-testid={testid}
        />
        {options?.length > 0 && (
          <button
            type="button"
            onClick={() => setMode("select")}
            className="text-[10px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] px-2"
            title="Pick from list"
          >
            ↺
          </button>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // MOBILE: inline picker. Rendered inside the Sheet DOM so
  //   `react-remove-scroll` treats it as safe to scroll on iOS.
  // ────────────────────────────────────────────────────────────────
  if (IS_MOBILE) {
    return (
      <div className="flex gap-1.5">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 min-w-0 flex items-center justify-between gap-2 h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-left text-[#0A0604] active:bg-[#F4E9C9]"
          data-testid={testid}
        >
          <span className={`truncate ${value ? "" : "text-[#3A2A10]/60"}`}>
            {value || placeholder}
          </span>
          <ChevronsUpDown size={14} className="text-[#3A2A10] shrink-0" />
        </button>
        {!lookupOnly && (
          <button
            type="button"
            onClick={() => setMode("input")}
            className="text-[10px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] px-2"
            title="Type custom"
          >
            <Pencil size={12} />
          </button>
        )}

        {open && (
          <MobileInlinePicker
            options={allOptions}
            value={value}
            onSelect={(v) => { onChange(v); setOpen(false); }}
            onClose={() => setOpen(false)}
            testid={testid}
          />
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // DESKTOP: Radix popover — big scrollable list works fine here.
  // ────────────────────────────────────────────────────────────────
  return (
    <div className="flex gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className="flex-1 min-w-0 flex items-center justify-between gap-2 h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-left text-[#0A0604] hover:bg-[#F4E9C9]"
            data-testid={testid}
          >
            <span className={`truncate ${value ? "" : "text-[#3A2A10]/60"}`}>
              {value || placeholder}
            </span>
            <ChevronsUpDown size={14} className="text-[#3A2A10]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] bg-[#FBF3D9] border border-[#8B6A1F]/45 rounded-sm shadow-lg overflow-hidden"
          style={{
            maxHeight: "min(65vh, var(--radix-popover-content-available-height, 65vh))",
          }}
          align="start"
          sideOffset={4}
          avoidCollisions
          collisionPadding={12}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            className="overflow-y-auto overscroll-contain"
            style={{
              maxHeight: "inherit",
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {allOptions.length === 0 && (
              <div className="px-3 py-3 text-sm text-[#3A2A10] italic">No options</div>
            )}
            {allOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); }}
                className="flex items-center justify-between w-full text-left px-3 py-2.5 text-sm text-[#0A0604] hover:bg-[#2B2214]/[0.06] border-b border-[#8B6A1F]/15 last:border-0"
                data-testid={`${testid}-option-${opt}`}
              >
                <span>{opt}</span>
                {String(value) === opt && <Check size={13} className="text-[#D4AF37]" />}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {!lookupOnly && (
        <button
          type="button"
          onClick={() => setMode("input")}
          className="text-[10px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] px-2"
          title="Type custom"
        >
          <Pencil size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * Inline mobile picker. Sits inside the Sheet DOM subtree — critical for
 * iOS Safari, where `react-remove-scroll` (used by Radix Dialog) globally
 * preventDefaults touchmove events outside its subtree, killing scroll.
 *
 * Sizes to content up to a 70vh cap. Positioned as a fixed centered
 * overlay so it doesn't push the form around.
 */
function MobileInlinePicker({
  options, value, onSelect, onClose, testid,
}) {
  const listRef = useRef(null);

  // Scroll the currently-selected value into view on mount so the user
  // lands near their current pick, not at the top of 300+ items.
  useEffect(() => {
    if (!listRef.current || !value) return;
    const target = listRef.current.querySelector(
      `[data-testid="${testid}-mobile-option-${CSS.escape(String(value))}"]`
    );
    if (target) target.scrollIntoView({ block: "center" });
  }, [value, testid]);

  return (
    // We render TWO siblings: a backdrop and the actual picker card.
    // Both use position:fixed so they escape any parent overflow/scroll
    // clipping (Sheet content scrolls its own body).
    <>
      {/* Backdrop — closes on tap */}
      <div
        className="fixed inset-0 z-[95] bg-black/40"
        onClick={onClose}
        onTouchStart={(e) => e.stopPropagation()}
        data-testid={`${testid}-mobile-backdrop`}
      />

      {/* Picker card — centered, sized to content up to 70vh */}
      <div
        className="fixed left-3 right-3 z-[96] bg-[#FBF3D9] border border-[#8B6A1F]/45 rounded-md shadow-2xl flex flex-col overflow-hidden"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          maxHeight: "70vh",
        }}
        data-testid={`${testid}-mobile-sheet`}
      >
        {/* Small close-only header — no title bar taking up space */}
        <div className="flex items-center justify-end px-2 py-1.5 border-b border-[#8B6A1F]/20 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-[#3A2A10]"
            aria-label="Close"
            data-testid={`${testid}-mobile-close`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable list. `touch-action: pan-y` tells iOS this element
            handles vertical touch scrolling. Combined with
            `-webkit-overflow-scrolling: touch` for momentum. Because we
            are INSIDE the Sheet's react-remove-scroll subtree, touchmove
            is NOT prevented here. */}
        <div
          ref={listRef}
          className="overflow-y-auto overscroll-contain"
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
        >
          {options.length === 0 && (
            <div className="px-4 py-6 text-sm text-[#3A2A10] italic text-center">
              No options
            </div>
          )}
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onSelect(opt)}
              className="flex items-center justify-between w-full text-left px-4 py-3 text-base text-[#0A0604] active:bg-[#2B2214]/[0.08] border-b border-[#8B6A1F]/15 last:border-0"
              data-testid={`${testid}-mobile-option-${opt}`}
            >
              <span>{opt}</span>
              {String(value) === opt && <Check size={16} className="text-[#D4AF37]" />}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
